import { musicianServices } from "./pricingData";
import { majorAirports } from "./airports";

export interface TieredPackage {
  tier: "good" | "better" | "best";
  name: string;
  musicians: string[];
  moment: string;
  description: string;
  basePrice: number;
  duration: string;
  guestCountMin: number;
  guestCountMax: number;
  features: string[];
}

export interface PackageTierSet {
  moment: string;
  name?: string;
  good: TieredPackage;
  better: TieredPackage;
  best: TieredPackage;
}

export interface MultiMomentPackage {
  id: string;
  name: string;
  moments: string[];
  packages: TieredPackage[];
  musicians: string[];
  totalBasePrice: number;
  totalPrice: number;
  savings: number;
  duration: string;
  description: string;
  features: string[];
}

export interface PrebuiltMultiMomentPackageSet {
  id: string;
  name: string;
  moments: string[];
  description: string;
  good: { musicians: string[]; features: string[] };
  better: { musicians: string[]; features: string[] };
  best: { musicians: string[]; features: string[] };
}

export interface PricingAdjustments {
  basePrice: number;
  distanceSurcharge: number;
  guestCountAdjustment: number;
  locationSurcharge: number;
  rushHourSurcharge: number;
  remoteSurcharge: number;
  finalPrice: number;
  adjustmentDetails: {
    nearestAirport: string;
    distanceMiles: number;
    isMetroArea: boolean;
    isRemote: boolean;
  };
}

// Regional manager buffer components (per event window, not per date)
const REGIONAL_MANAGER_BUFFER = {
  airfare: 500,
  accommodation: 200,
  transport: 100,
  meals: 60, // full day
};
const TOTAL_RM_BUFFER = 860;

// Musician meal per performance date
const MUSICIAN_MEAL_PER_DATE = 15;

// Profit margins
const MUSICIAN_MARGIN = 0.0; // No margin - pass through musician costs for competitive pricing
const BUFFER_MARGIN = 0.25; // 25% margin on operational buffers

// Get musician cost using rollup1Hr (includes travel for 1 hour)
function getMusicianCost(musicianName: string): number {
  const musician = musicianServices.find(m => m.name === musicianName);
  return musician ? musician.rollup1Hr : 1125;
}

// Calculate haversine distance between two coordinates
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

// Find nearest airport and calculate distance
function findNearestAirport(venueLat: number, venueLng: number): { airport: string; distance: number } {
  let nearest = { airport: "Unknown", distance: 999999 };
  
  majorAirports.forEach(airport => {
    const distance = calculateDistance(venueLat, venueLng, airport.lat, airport.lng);
    if (distance < nearest.distance) {
      nearest = { airport: airport.city, distance };
    }
  });
  
  return nearest;
}

// Major metro areas (for rush hour surcharge)
const MAJOR_METROS = [
  { city: "New York", lat: 40.7128, lng: -74.0060, radius: 50 },
  { city: "Los Angeles", lat: 34.0522, lng: -118.2437, radius: 50 },
  { city: "Chicago", lat: 41.8781, lng: -87.6298, radius: 40 },
  { city: "San Francisco", lat: 37.7749, lng: -122.4194, radius: 40 },
  { city: "Boston", lat: 42.3601, lng: -71.0589, radius: 35 },
  { city: "Washington DC", lat: 38.9072, lng: -77.0369, radius: 35 },
  { city: "Miami", lat: 25.7617, lng: -80.1918, radius: 30 },
  { city: "Dallas", lat: 32.7767, lng: -96.7970, radius: 35 },
  { city: "Houston", lat: 29.7604, lng: -95.3698, radius: 35 },
  { city: "Atlanta", lat: 33.7490, lng: -84.3880, radius: 35 },
  { city: "Seattle", lat: 47.6062, lng: -122.3321, radius: 30 },
];

// Check if venue is in major metro area
function isInMetroArea(venueLat: number, venueLng: number): boolean {
  return MAJOR_METROS.some(metro => {
    const distance = calculateDistance(venueLat, venueLng, metro.lat, metro.lng);
    return distance <= metro.radius;
  });
}

// Calculate smart pricing adjustments - MINIMAL to preserve ensemble size ladder
export function calculateSmartPricing(
  basePrice: number,
  venueLat: number,
  venueLng: number,
  guestCount: number,
  packageGuestMin: number,
  packageGuestMax: number
): PricingAdjustments {
  // Minimal adjustments to preserve clear ensemble size differences
  const MAX_TOTAL_ADJUSTMENT = 800; // Keep adjustments small
  
  // If no valid venue coordinates, use base price with no adjustments
  if (venueLat === 0 && venueLng === 0) {
    return {
      basePrice,
      distanceSurcharge: 0,
      guestCountAdjustment: 0,
      locationSurcharge: 0,
      rushHourSurcharge: 0,
      remoteSurcharge: 0,
      finalPrice: basePrice,
      adjustmentDetails: {
        nearestAirport: 'N/A',
        distanceMiles: 0,
        isMetroArea: false,
        isRemote: false,
      }
    };
  }
  
  const nearest = findNearestAirport(venueLat, venueLng);
  const isMetro = isInMetroArea(venueLat, venueLng);
  const isRemote = nearest.distance > 100;
  
  // Small distance surcharge: $20 per 20 miles over 50 miles, capped at $300
  let distanceSurcharge = 0;
  if (nearest.distance > 50) {
    distanceSurcharge = Math.min(
      Math.ceil((nearest.distance - 50) / 20) * 20,
      300
    );
  }
  
  // Minimal guest count adjustment, capped at $300
  let guestCountAdjustment = 0;
  if (guestCount > packageGuestMax) {
    const overage = guestCount - packageGuestMax;
    guestCountAdjustment = Math.min(
      Math.ceil(overage / 100) * 100,
      300
    );
  } else if (guestCount < packageGuestMin && packageGuestMin > 0) {
    guestCountAdjustment = -50;
  }
  
  // Small metro surcharge
  const rushHourSurcharge = isMetro ? 100 : 0;
  
  // Small remote surcharge
  const remoteSurcharge = isRemote ? 200 : 0;
  
  // No additional location surcharge - included in remote
  const locationSurcharge = 0;
  
  // Calculate total adjustments
  let totalAdjustment = 
    distanceSurcharge + 
    guestCountAdjustment + 
    rushHourSurcharge + 
    remoteSurcharge + 
    locationSurcharge;
  
  // Cap total adjustments to preserve ensemble size ladder
  if (totalAdjustment > MAX_TOTAL_ADJUSTMENT) {
    const scale = MAX_TOTAL_ADJUSTMENT / totalAdjustment;
    distanceSurcharge = Math.round(distanceSurcharge * scale);
    guestCountAdjustment = Math.round(guestCountAdjustment * scale);
  } else if (totalAdjustment < -MAX_TOTAL_ADJUSTMENT) {
    const scale = MAX_TOTAL_ADJUSTMENT / Math.abs(totalAdjustment);
    guestCountAdjustment = Math.round(guestCountAdjustment * scale);
  }
  
  // Calculate final price
  let calculatedPrice = basePrice + 
    distanceSurcharge + 
    guestCountAdjustment + 
    rushHourSurcharge + 
    remoteSurcharge + 
    locationSurcharge;
  
  // Round to nearest $100
  const finalPrice = Math.round(calculatedPrice / 100) * 100;
  
  return {
    basePrice,
    distanceSurcharge,
    guestCountAdjustment,
    locationSurcharge,
    rushHourSurcharge,
    remoteSurcharge,
    finalPrice,
    adjustmentDetails: {
      nearestAirport: nearest.airport,
      distanceMiles: Math.round(nearest.distance),
      isMetroArea: isMetro,
      isRemote,
    }
  };
}
// Packages are for duo/trio/quatro (2-4 musicians) and include regional manager
function calculatePackagePrice(
  musicians: string[],
  numberOfDates: number = 1
): number {
  // Calculate base musician costs (rollup4Hr includes individual travel)
  const musicianCosts = musicians.reduce((total, musicianName) => {
    const cost = getMusicianCost(musicianName);
    return total + cost;
  }, 0);
  
  // Apply 50% margin to musician costs: Price = Cost / (1 - 0.5) = Cost × 2
  const musicianPriceWithMargin = musicianCosts / (1 - MUSICIAN_MARGIN);
  
  // Regional manager buffer (once per event window) with 25% margin
  const regionalManagerBuffer = TOTAL_RM_BUFFER / (1 - BUFFER_MARGIN);
  
  // Musician meals (per musician per date) with 25% margin
  const musicianMealBuffer = 
    (MUSICIAN_MEAL_PER_DATE * musicians.length * numberOfDates) / (1 - BUFFER_MARGIN);
  
  // Total price
  const totalPrice = 
    musicianPriceWithMargin + regionalManagerBuffer + musicianMealBuffer;
  
  // Round up to nearest $100
  return Math.ceil(totalPrice / 100) * 100;
}

export const packageTierSets: PackageTierSet[] = [
  {
    moment: "Cocktail Hour",
    good: {
      tier: "good",
      name: "Cocktail Duo",
      musicians: ["Violinists", "Cellists"],
      moment: "Cocktail Hour",
      description: "Elegant string duo for intimate cocktail ambiance",
      basePrice: calculatePackagePrice(["Violinists", "Cellists"]),
      duration: "Up to 4 hours",
      guestCountMin: 0,
      guestCountMax: 75,
      features: ["Professional string duo", "Curated repertoire", "Setup included", "Travel & meal expenses included"]
    },
    better: {
      tier: "better",
      name: "Cocktail Strings Trio",
      musicians: ["Violinists", "Cellists", "Viola Players"],
      moment: "Cocktail Hour",
      description: "Classic string trio with richer harmony",
      basePrice: calculatePackagePrice(["Violinists", "Cellists", "Viola Players"]),
      duration: "Up to 4 hours",
      guestCountMin: 75,
      guestCountMax: 150,
      features: ["String trio ensemble", "Song requests available", "Regional manager coordination", "All travel, accommodation & meal expenses included"]
    },
    best: {
      tier: "best",
      name: "Cocktail String Quartet",
      musicians: ["Violinists", "Violinists", "Cellists", "Viola Players"],
      moment: "Cocktail Hour",
      description: "Full string quartet for sophisticated elegance",
      basePrice: calculatePackagePrice(["Violinists", "Violinists", "Cellists", "Viola Players"]),
      duration: "Up to 4 hours",
      guestCountMin: 150,
      guestCountMax: 500,
      features: ["Full quartet", "Custom song arrangements", "Dedicated manager", "All travel, accommodation, transport & meal expenses included"]
    }
  },
  {
    moment: "Indian Ceremony",
    good: {
      tier: "good",
      name: "Sacred Duo",
      musicians: ["Shehnai Players", "Tabla Players"],
      moment: "Indian Ceremony",
      description: "Traditional shehnai and tabla for ceremonies",
      basePrice: calculatePackagePrice(["Shehnai Players", "Tabla Players"]),
      duration: "Up to 4 hours",
      guestCountMin: 0,
      guestCountMax: 100,
      features: ["Shehnai & tabla", "Traditional repertoire", "Ceremony coordination", "Travel & meal expenses included"]
    },
    better: {
      tier: "better",
      name: "Ceremony Trio",
      musicians: ["Shehnai Players", "Tabla Players", "Indian Classical Strings"],
      moment: "Indian Ceremony",
      description: "Full Indian classical ensemble with strings",
      basePrice: calculatePackagePrice(["Shehnai Players", "Tabla Players", "Indian Classical Strings"]),
      duration: "Up to 4 hours",
      guestCountMin: 100,
      guestCountMax: 250,
      features: ["Traditional trio", "Processional music", "Regional manager on-site", "All travel, accommodation & meal expenses included"]
    },
    best: {
      tier: "best",
      name: "Grand Ceremony Ensemble",
      musicians: ["Shehnai Players", "Tabla Players", "Indian Classical Strings", "Dhol Players"],
      moment: "Indian Ceremony",
      description: "Premium Indian ceremony with complete ensemble",
      basePrice: calculatePackagePrice(["Shehnai Players", "Tabla Players", "Indian Classical Strings", "Dhol Players"]),
      duration: "Up to 4 hours",
      guestCountMin: 250,
      guestCountMax: 1000,
      features: ["Full traditional ensemble", "Custom arrangements", "Dedicated manager", "Pre-event consultation", "All travel, accommodation, transport & meal expenses included"]
    }
  },
  {
    moment: "Reception/Dance Floor",
    good: {
      tier: "good",
      name: "Dance Duo",
      musicians: ["Guitarists", "Drummers"],
      moment: "Reception/Dance Floor",
      description: "Energetic duo to get your guests dancing",
      basePrice: calculatePackagePrice(["Guitarists", "Drummers"]),
      duration: "Up to 4 hours",
      guestCountMin: 0,
      guestCountMax: 100,
      features: ["Live band duo", "Popular dance hits", "Professional sound system", "Travel & meal expenses included"]
    },
    better: {
      tier: "better",
      name: "Live Band Quartet",
      musicians: ["Guitarists", "Drummers", "Bassists", "Saxophonists"],
      moment: "Reception/Dance Floor",
      description: "Complete live band keeping the dance floor packed",
      basePrice: calculatePackagePrice(["Guitarists", "Drummers", "Bassists", "Saxophonists"]),
      duration: "Up to 4 hours",
      guestCountMin: 100,
      guestCountMax: 300,
      features: ["Full live band", "High-energy repertoire", "Professional sound & lighting", "Regional manager coordination", "All travel, accommodation & meal expenses included"]
    },
    best: {
      tier: "best",
      name: "Premium 6-Piece Band",
      musicians: ["Guitarists", "Drummers", "Bassists", "Saxophonists", "Trumpet Players", "Pianists"],
      moment: "Reception/Dance Floor",
      description: "Elite live band delivering unforgettable reception",
      basePrice: calculatePackagePrice(["Guitarists", "Drummers", "Bassists", "Saxophonists", "Trumpet Players", "Pianists"]),
      duration: "Up to 4 hours",
      guestCountMin: 300,
      guestCountMax: 1000,
      features: ["Premium 6-piece band", "Full horn section", "Premium sound & lighting", "Custom setlist", "Pre-event rehearsal", "All travel, accommodation, transport & meal expenses included"]
    }
  },
  {
    moment: "Dinner Service",
    good: {
      tier: "good",
      name: "Dinner Duo",
      musicians: ["Violinists", "Cellists"],
      moment: "Dinner Service",
      description: "Classic string duo for refined dinner atmosphere",
      basePrice: calculatePackagePrice(["Violinists", "Cellists"]),
      duration: "Up to 4 hours",
      guestCountMin: 0,
      guestCountMax: 75,
      features: ["String duo", "Elegant background music", "Professional sound system", "Travel & meal expenses included"]
    },
    better: {
      tier: "better",
      name: "Dinner Strings with Harp",
      musicians: ["Harpists", "Violinists", "Cellists"],
      moment: "Dinner Service",
      description: "Luxurious harp and strings for exquisite dinner",
      basePrice: calculatePackagePrice(["Harpists", "Violinists", "Cellists"]),
      duration: "Up to 4 hours",
      guestCountMin: 75,
      guestCountMax: 200,
      features: ["String trio with harp", "Sophisticated ambiance", "Music consultation", "Regional manager coordination", "All travel, accommodation & meal expenses included"]
    },
    best: {
      tier: "best",
      name: "Elite Dinner Ensemble",
      musicians: ["Harpists", "Violinists", "Cellists", "Pianists"],
      moment: "Dinner Service",
      description: "Elite ensemble creating unforgettable dinner ambiance",
      basePrice: calculatePackagePrice(["Harpists", "Violinists", "Cellists", "Pianists"]),
      duration: "Up to 4 hours",
      guestCountMin: 200,
      guestCountMax: 1000,
      features: ["String quartet with harp & piano", "Premium dining experience", "Custom setlist curation", "Pre-event consultation", "All travel, accommodation, transport & meal expenses included"]
    }
  },
  {
    moment: "Lunch Service",
    good: {
      tier: "good",
      name: "Lunch Duo",
      musicians: ["Guitarists", "Violinists"],
      moment: "Lunch Service",
      description: "Gentle acoustic duo for sophisticated lunch",
      basePrice: calculatePackagePrice(["Guitarists", "Violinists"]),
      duration: "Up to 4 hours",
      guestCountMin: 0,
      guestCountMax: 75,
      features: ["Acoustic duo", "Light background music", "Professional sound system", "Travel & meal expenses included"]
    },
    better: {
      tier: "better",
      name: "Elegant Lunch Ensemble",
      musicians: ["Harpists", "Violinists", "Cellists"],
      moment: "Lunch Service",
      description: "Sophisticated ensemble with harp for elegant lunch",
      basePrice: calculatePackagePrice(["Harpists", "Violinists", "Cellists"]),
      duration: "Up to 4 hours",
      guestCountMin: 75,
      guestCountMax: 200,
      features: ["String trio with harp", "Refined selections", "Music consultation", "Regional manager coordination", "All travel, accommodation & meal expenses included"]
    },
    best: {
      tier: "best",
      name: "Premium Lunch Experience",
      musicians: ["Pianists", "Saxophonists", "Bassists", "Violinists"],
      moment: "Lunch Service",
      description: "Premium jazz & strings for luxury lunch",
      basePrice: calculatePackagePrice(["Pianists", "Saxophonists", "Bassists", "Violinists"]),
      duration: "Up to 4 hours",
      guestCountMin: 200,
      guestCountMax: 1000,
      features: ["Jazz ensemble with strings", "Upscale repertoire", "Custom music curation", "Pre-event consultation", "All travel, accommodation, transport & meal expenses included"]
    }
  },
  {
    moment: "Baraat/Procession",
    good: {
      tier: "good",
      name: "Dhol Energy",
      musicians: ["Dhol Players"],
      moment: "Baraat/Procession",
      description: "Traditional dhol creating energetic baraat atmosphere",
      basePrice: calculatePackagePrice(["Dhol Players"]),
      duration: "Up to 4 hours",
      guestCountMin: 0,
      guestCountMax: 150,
      features: ["High-energy dhol", "Traditional baraat music", "Portable sound system", "Travel & meal expenses included"]
    },
    better: {
      tier: "better",
      name: "Grand Baraat Ensemble",
      musicians: ["Dhol Players", "Shehnai Players", "Tabla Players"],
      moment: "Baraat/Procession",
      description: "Full traditional ensemble for grand baraat entrance",
      basePrice: calculatePackagePrice(["Dhol Players", "Shehnai Players", "Tabla Players"]),
      duration: "Up to 4 hours",
      guestCountMin: 150,
      guestCountMax: 400,
      features: ["Complete baraat ensemble", "Traditional processional music", "Procession timing coordination", "Regional manager coordination", "All travel, accommodation & meal expenses included"]
    },
    best: {
      tier: "best",
      name: "Spectacular Baraat",
      musicians: ["Dhol Players", "Shehnai Players", "Tabla Players", "Indian Classical Strings"],
      moment: "Baraat/Procession",
      description: "Spectacular ensemble for unforgettable baraat",
      basePrice: calculatePackagePrice(["Dhol Players", "Shehnai Players", "Tabla Players", "Indian Classical Strings"]),
      duration: "Up to 4 hours",
      guestCountMin: 400,
      guestCountMax: 1000,
      features: ["Premium baraat ensemble", "Grand processional experience", "Custom arrangements", "Full event timing coordination", "All travel, accommodation, transport & meal expenses included"]
    }
  },
  {
    moment: "First Dance",
    good: {
      tier: "good",
      name: "First Dance Duo",
      musicians: ["Violinists", "Guitarists"],
      moment: "First Dance",
      description: "Intimate acoustic duo for your first dance moment",
      basePrice: calculatePackagePrice(["Violinists", "Guitarists"]),
      duration: "Up to 4 hours",
      guestCountMin: 0,
      guestCountMax: 100,
      features: ["Acoustic duo", "Custom song arrangement", "Professional sound system", "Travel & meal expenses included"]
    },
    better: {
      tier: "better",
      name: "First Dance Trio",
      musicians: ["Violinists", "Cellists", "Pianists"],
      moment: "First Dance",
      description: "Elegant classical trio for unforgettable first dance",
      basePrice: calculatePackagePrice(["Violinists", "Cellists", "Pianists"]),
      duration: "Up to 4 hours",
      guestCountMin: 100,
      guestCountMax: 300,
      features: ["Classical trio", "Professional arrangement", "Premium sound system", "Pre-event consultation", "Regional manager coordination", "All travel, accommodation & meal expenses included"]
    },
    best: {
      tier: "best",
      name: "Magical First Dance",
      musicians: ["Violinists", "Cellists", "Harpists", "Pianists"],
      moment: "First Dance",
      description: "Luxurious ensemble creating magical first dance",
      basePrice: calculatePackagePrice(["Violinists", "Cellists", "Harpists", "Pianists"]),
      duration: "Up to 4 hours",
      guestCountMin: 300,
      guestCountMax: 1000,
      features: ["String quartet with harp", "Custom orchestral arrangement", "Premium sound & lighting", "Dedicated rehearsal", "Full music direction", "All travel, accommodation, transport & meal expenses included"]
    }
  },
  {
    moment: "Grand Entrance",
    good: {
      tier: "good",
      name: "Entrance Duo",
      musicians: ["Trumpet Players", "Drummers"],
      moment: "Grand Entrance",
      description: "Bold brass duo announcing your grand entrance",
      basePrice: calculatePackagePrice(["Trumpet Players", "Drummers"]),
      duration: "Up to 4 hours",
      guestCountMin: 0,
      guestCountMax: 150,
      features: ["Brass duo", "High-impact entrance music", "Portable sound system", "Travel & meal expenses included"]
    },
    better: {
      tier: "better",
      name: "Spectacular Entrance",
      musicians: ["Trumpet Players", "Trombonists", "Saxophonists", "Drummers"],
      moment: "Grand Entrance",
      description: "Complete brass ensemble creating spectacular entrance",
      basePrice: calculatePackagePrice(["Trumpet Players", "Trombonists", "Saxophonists", "Drummers"]),
      duration: "Up to 4 hours",
      guestCountMin: 150,
      guestCountMax: 400,
      features: ["Full brass ensemble", "Powerful entrance fanfare", "Professional sound system", "Entrance timing coordination", "Regional manager coordination", "All travel, accommodation & meal expenses included"]
    },
    best: {
      tier: "best",
      name: "Epic Grand Entrance",
      musicians: ["Trumpet Players", "Trombonists", "Saxophonists", "Drummers", "Dhol Players"],
      moment: "Grand Entrance",
      description: "Elite ensemble delivering unforgettable entrance",
      basePrice: calculatePackagePrice(["Trumpet Players", "Trombonists", "Saxophonists", "Drummers", "Dhol Players"]),
      duration: "Up to 4 hours",
      guestCountMin: 400,
      guestCountMax: 1000,
      features: ["Premium brass & percussion", "Epic entrance production", "Premium sound & lighting", "Full choreographed experience", "Pre-event timing rehearsal", "All travel, accommodation, transport & meal expenses included"]
    }
  },
  {
    moment: "After Party",
    good: {
      tier: "good",
      name: "After Party Duo",
      musicians: ["Guitarists", "Drummers"],
      moment: "After Party",
      description: "Relaxed duo keeping the after party vibe alive",
      basePrice: calculatePackagePrice(["Guitarists", "Drummers"]),
      duration: "Up to 4 hours",
      guestCountMin: 0,
      guestCountMax: 100,
      features: ["Acoustic duo", "Casual party atmosphere", "Professional sound system", "Travel & meal expenses included"]
    },
    better: {
      tier: "better",
      name: "After Party Trio",
      musicians: ["Guitarists", "Drummers", "Bassists"],
      moment: "After Party",
      description: "Energetic trio delivering non-stop entertainment",
      basePrice: calculatePackagePrice(["Guitarists", "Drummers", "Bassists"]),
      duration: "Up to 4 hours",
      guestCountMin: 100,
      guestCountMax: 300,
      features: ["Rock trio", "High-energy party music", "Flexible setlist", "Regional manager coordination", "All travel, accommodation & meal expenses included"]
    },
    best: {
      tier: "best",
      name: "Premium After Party",
      musicians: ["Guitarists", "Drummers", "Bassists", "Saxophonists", "Pianists"],
      moment: "After Party",
      description: "Premium party band ensuring unforgettable experience",
      basePrice: calculatePackagePrice(["Guitarists", "Drummers", "Bassists", "Saxophonists", "Pianists"]),
      duration: "Up to 4 hours",
      guestCountMin: 300,
      guestCountMax: 1000,
      features: ["Full party band", "Premium party experience", "Extensive repertoire", "DJ integration available", "All travel, accommodation, transport & meal expenses included"]
    }
  },
  {
    moment: "Jewish Ceremony",
    good: {
      tier: "good",
      name: "Jewish Ceremony Duo",
      musicians: ["Violinists", "Cellists"],
      moment: "Jewish Ceremony",
      description: "Classic string duo for Jewish wedding ceremony",
      basePrice: calculatePackagePrice(["Violinists", "Cellists"]),
      duration: "Up to 4 hours",
      guestCountMin: 0,
      guestCountMax: 100,
      features: ["String duo", "Traditional Jewish music", "Processional & recessional", "Travel & meal expenses included"]
    },
    better: {
      tier: "better",
      name: "Jewish Ceremony Trio",
      musicians: ["Violinists", "Cellists", "Harpists"],
      moment: "Jewish Ceremony",
      description: "Elegant ensemble for Jewish wedding traditions",
      basePrice: calculatePackagePrice(["Violinists", "Cellists", "Harpists"]),
      duration: "Up to 4 hours",
      guestCountMin: 100,
      guestCountMax: 300,
      features: ["String trio with harp", "Traditional & contemporary music", "Full ceremony coverage", "Music consultation", "Regional manager coordination", "All travel, accommodation & meal expenses included"]
    },
    best: {
      tier: "best",
      name: "Premium Jewish Ceremony",
      musicians: ["Violinists", "Cellists", "Harpists", "Trumpet Players"],
      moment: "Jewish Ceremony",
      description: "Premium ensemble honoring Jewish traditions",
      basePrice: calculatePackagePrice(["Violinists", "Cellists", "Harpists", "Trumpet Players"]),
      duration: "Up to 4 hours",
      guestCountMin: 300,
      guestCountMax: 1000,
      features: ["String quartet with harp & trumpet", "Complete Jewish repertoire", "Premium ceremony experience", "Custom arrangements", "Pre-ceremony consultation", "All travel, accommodation, transport & meal expenses included"]
    }
  },
  {
    moment: "Chinese Wedding Ceremony",
    good: {
      tier: "good",
      name: "Chinese Ceremony Duo",
      musicians: ["Violinists", "Guitarists"],
      moment: "Chinese Wedding Ceremony",
      description: "Elegant duo for Chinese wedding ceremony",
      basePrice: calculatePackagePrice(["Violinists", "Guitarists"]),
      duration: "Up to 4 hours",
      guestCountMin: 0,
      guestCountMax: 100,
      features: ["Acoustic duo", "Traditional & modern Chinese music", "Ceremony processional", "Travel & meal expenses included"]
    },
    better: {
      tier: "better",
      name: "Chinese Ceremony Trio",
      musicians: ["Violinists", "Cellists", "Pianists"],
      moment: "Chinese Wedding Ceremony",
      description: "Refined ensemble for Chinese wedding traditions",
      basePrice: calculatePackagePrice(["Violinists", "Cellists", "Pianists"]),
      duration: "Up to 4 hours",
      guestCountMin: 100,
      guestCountMax: 300,
      features: ["String trio", "Traditional & contemporary blend", "Tea ceremony coverage", "Bilingual consultation", "Regional manager coordination", "All travel, accommodation & meal expenses included"]
    },
    best: {
      tier: "best",
      name: "Premium Chinese Ceremony",
      musicians: ["Violinists", "Cellists", "Harpists", "Pianists"],
      moment: "Chinese Wedding Ceremony",
      description: "Luxurious ensemble celebrating Chinese culture",
      basePrice: calculatePackagePrice(["Violinists", "Cellists", "Harpists", "Pianists"]),
      duration: "Up to 4 hours",
      guestCountMin: 300,
      guestCountMax: 1000,
      features: ["Premium string quartet", "Full Chinese ceremony repertoire", "Tea ceremony through reception", "Custom arrangements", "Pre-event consultation", "All travel, accommodation, transport & meal expenses included"]
    }
  }
];

// Prebuilt multi-moment packages
export const prebuiltMultiMomentPackages: PrebuiltMultiMomentPackageSet[] = [
  {
    id: "full-day-wedding",
    name: "Full Day Wedding Package",
    moments: ["Indian Ceremony", "Cocktail Hour", "Reception/Dance Floor"],
    description: "Complete coverage for your wedding day from ceremony to reception",
    good: {
      musicians: ["Shehnai Players", "Tabla Players", "Violinists", "Cellists", "Guitarists", "Drummers"],
      features: ["Multi-moment coverage", "6 musicians total", "Seamless transitions", "All travel & meal expenses included"]
    },
    better: {
      musicians: ["Shehnai Players", "Tabla Players", "Indian Classical Strings", "Violinists", "Cellists", "Pianists", "Guitarists", "Drummers", "Bassists", "Saxophonists"],
      features: ["Multi-moment coverage", "10 musicians total", "Regional manager coordination", "Custom arrangements", "All travel, accommodation & meal expenses included"]
    },
    best: {
      musicians: ["Shehnai Players", "Tabla Players", "Indian Classical Strings", "Dhol Players", "Violinists", "Cellists", "Harpists", "Pianists", "Guitarists", "Drummers", "Bassists", "Saxophonists", "Trumpet Players"],
      features: ["Premium multi-moment coverage", "13+ musicians", "Dedicated event manager", "Full custom arrangements", "Pre-event rehearsals", "All travel, accommodation, transport & meal expenses included"]
    }
  }
];

// Calculate prebuilt package prices
export function calculatePrebuiltPackagePrices(packageSet: PrebuiltMultiMomentPackageSet, distance: number = 0): {
  good: MultiMomentPackage;
  better: MultiMomentPackage;
  best: MultiMomentPackage;
} {
  const tiers: ("good" | "better" | "best")[] = ["good", "better", "best"];
  const result = {} as any;
  
  tiers.forEach(tier => {
    const config = packageSet[tier];
    const basePrice = calculatePackagePrice(config.musicians);
    const adjustedPrice = adjustPriceForDistance(basePrice, distance);
    
    result[tier] = {
      id: `${packageSet.id}-${tier}`,
      name: `${packageSet.name} - ${tier.charAt(0).toUpperCase() + tier.slice(1)}`,
      moments: packageSet.moments,
      packages: [],
      musicians: config.musicians,
      totalBasePrice: basePrice,
      totalPrice: adjustedPrice,
      savings: 0,
      duration: "Up to 4 hours per moment",
      description: packageSet.description,
      features: config.features
    };
  });
  
  return result;
}

// Adjust price for distance
export function adjustPriceForDistance(basePrice: number, distance: number): number {
  const multiplier = getPriceMultiplierForDistance(distance);
  return Math.ceil((basePrice * multiplier) / 100) * 100;
}

// Get recommended tier based on guest count
export function getRecommendedTier(guestCount: number): "good" | "better" | "best" {
  if (guestCount <= 100) return "good";
  if (guestCount <= 300) return "better";
  return "best";
}

export const getPriceMultiplierForDistance = (distance: number): number => {
  if (distance < 100) return 1.0;
  if (distance < 500) return 1.1;
  if (distance < 1000) return 1.2;
  if (distance < 2000) return 1.3;
  return 1.5;
};
